import { useCallback, useRef } from 'react';

import type { AvailableSite } from '@atlassian/gic-anywhere';
import { useGetFirstAvailableSiteDomain } from '@atlassian/gic-anywhere';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { fg } from '@confluence/feature-gating';

import {
	ErrorStates,
	useBulkCreateContext,
	useSetError,
} from '../providers/BulkCreateContextProvider.tsx';
import {
	useAvailableSitesActions,
	useAvailableSitesState,
} from '../providers/BulkConfigureContextProvider.tsx';
import { getPersistedOverrideCloudId } from '../utils/localStorageHelpers.ts';
import { useBulkCreateLoggingEvents } from '../utils/useBulkCreateLoggingEvents.tsx';

export const useGetAvailableSites = () => {
	const { setSelectedAvailableSite, setOptionsAvailableSites } = useAvailableSitesActions();
	const { selectedAvailableSite } = useAvailableSitesState();
	const { cloudId: sessionCloudId } = useSessionData();

	const persistedOverrideCloudId = getPersistedOverrideCloudId();

	const overrideCloudIdForSitePicker = persistedOverrideCloudId || sessionCloudId;
	const { logRequestFailed } = useBulkCreateLoggingEvents();
	const setError = useSetError();
	// eslint-disable-next-line confluence-sweet-state/sweet-state-prefer-actions-hook
	const [, actions] = useBulkCreateContext();

	const selectedAvailableSiteRef = useRef(selectedAvailableSite);
	selectedAvailableSiteRef.current = selectedAvailableSite;

	useGetFirstAvailableSiteDomain({
		preferredCloudId: overrideCloudIdForSitePicker,
		onComplete: useCallback(
			({ availableSites }: { availableSites: AvailableSite[] }) => {
				const firstSite = availableSites[0];
				const preferredSite = availableSites.find(
					(site: AvailableSite) => site.cloudId === overrideCloudIdForSitePicker,
				);
				const newSite = preferredSite || firstSite;
				// Only update the selected site if it has changed to avoid resetting downstream selections
				if (
					selectedAvailableSiteRef.current?.cloudId !== newSite?.cloudId ||
					!fg('confluence-create-work-items-from-action-items')
				) {
					void setSelectedAvailableSite(newSite);
				}
				void setOptionsAvailableSites(availableSites);
			},
			[setSelectedAvailableSite, setOptionsAvailableSites, overrideCloudIdForSitePicker],
		),
		onError: useCallback(
			(error: Error) => {
				logRequestFailed({ requestName: 'fetch-jira-site', errorMessage: error.message });
				void setError(ErrorStates.SITE_RETRIEVAL);
				void actions.setManualCreateState('error');
			},
			[logRequestFailed, setError, actions],
		),
	});
};
